import { createContext, useCallback, useContext, useState } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
  AlertDialogCancel,
} from "./ui/alert-dialog";

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null);

  const confirm = useCallback((options) => {
    const opts = typeof options === "string" ? { title: options } : options;
    return new Promise((resolve) => {
      setState({
        title: opts.title,
        description: opts.description,
        confirmLabel: opts.confirmLabel ?? "Confirm",
        cancelLabel: opts.cancelLabel ?? "Cancel",
        danger: opts.danger ?? false,
        resolve,
      });
    });
  }, []);

  const settle = (result) => {
    state?.resolve(result);
    setState(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={!!state} onOpenChange={(open) => { if (!open) settle(false); }}>
        <AlertDialogContent data-testid="confirm-dialog" className="bg-white border border-neutral-200 rounded-lg shadow-xl max-w-sm p-6">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading font-light text-xl tracking-tight text-[#111]">{state?.title}</AlertDialogTitle>
            {state?.description && (
              <AlertDialogDescription className="text-sm text-neutral-500 leading-relaxed">{state.description}</AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-2 gap-2 sm:gap-2">
            <AlertDialogCancel
              data-testid="confirm-dialog-cancel"
              onClick={() => settle(false)}
              className="rounded-full border-neutral-300 text-neutral-600 shadow-none hover:bg-neutral-100 hover:text-[#111]">
              {state?.cancelLabel}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-dialog-confirm"
              onClick={() => settle(true)}
              className={`rounded-full shadow-none text-white ${state?.danger ? "bg-red-600 hover:bg-red-700" : "bg-[#111] hover:bg-neutral-800"}`}>
              {state?.confirmLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  return useContext(ConfirmContext);
}
